import { useEffect, useMemo } from 'react';

import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ReportComponent } from '@/types';
import { DatasetQuery, QueryResult, ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import { compileComponentToQuery } from './compilers';
import { normalizeReportComponentConfiguration } from './configuration';
import { evaluateComponentReadiness } from './evaluateComponentReadiness';

const EMPTY_QUERY_RESULT: QueryResult = {
    rows: [],
    meta: { rowCount: 0 },
};

export const buildReportComponentDatasetQueryConfiguration = (
    component: ReportComponent,
    draft?: Partial<ReportComponentConfig>,
) => {
    const config = normalizeReportComponentConfiguration(
        {
            ...(component.configuration ?? {}),
            ...(draft ?? {}),
        },
        component.type,
    ) as ReportComponentConfig;
    const dataQuery = compileComponentToQuery(component, config);

    if (!config?.dataSet?.key || !dataQuery.datasetId || !dataQuery.metrics.length) {
        return null;
    }

    return {
        ...config,
        type: component.type,
        version: config?.version ?? 1,
        data: dataQuery,
    };
};

export const getReportComponentDatasetRequestKey = (componentId: string, configuration: unknown) =>
    JSON.stringify({
        componentId,
        configuration: canonicalizeForRequestKey(configuration),
    });

export const getDraftReportComponentDatasetQueryStateKey = (componentId: string) => `draft:${componentId}`;

export const getReportComponentDatasetQueryStateKey = (component: ReportComponent) =>
    (component as ReportComponent & { __datasetQueryStateKey?: string }).__datasetQueryStateKey ?? component.id;

const canonicalizeForRequestKey = (value: unknown): unknown => {
    if (Array.isArray(value)) {
        return value.map(canonicalizeForRequestKey);
    }

    if (!value || typeof value !== 'object') {
        return value;
    }

    return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
            acc[key] = canonicalizeForRequestKey((value as Record<string, unknown>)[key]);

            return acc;
        }, {});
};

export const useReportQuery = (component: ReportComponent): DatasetQuery => {
    const config = normalizeReportComponentConfiguration(
        component?.configuration,
        component.type,
    ) as ReportComponentConfig;

    return {
        datasetId: config?.dataSet?.key ?? '',
        metrics: config?.query?.metrics ?? [],
        dimensions: config?.query?.dimensions ?? [],
        filters: config?.query?.filters ?? undefined,
        dateRange: config?.query?.dateRange ?? undefined,
        limit: config?.query?.limit ?? 100,
    };
};

// Data Fetching

// export const useComponentQuery = (componentId: string) => {
//     const queryState = useDatasetStore((s) => s.componentQueryState[componentId]);

//     const rawData = queryState?.data as QueryResult;
//     const isLoading = queryState?.loading;
//     const error = queryState?.error;

//     return { rawData, isLoading, error };
// };

// export const useHydratedComponent = (component: ReportComponent) => {
//     const draft = useReportComponentStore((s) => s.draftConfigurations[component.id]);

//     return draft ? { ...component, configuration: draft } : component;
// };

export const useReportData = (component: ReportComponent) => {
    const publicQueryResult = (component as ReportComponent & { __publicQueryResult?: QueryResult }).__publicQueryResult;
    const queryStateKey = getReportComponentDatasetQueryStateKey(component);
    const queryState = useDatasetStore((state) => state.componentQueryState[queryStateKey]);
    const queryDatasets = useDatasetStore((state) => state.queryDatasets);
    const configurationKey = JSON.stringify(component.configuration ?? {});
    const readiness = useMemo(
        () => evaluateComponentReadiness(component),
        [component.id, component.type, configurationKey],
    );
    const queryConfiguration = useMemo(
        () => buildReportComponentDatasetQueryConfiguration(component),
        [component.id, component.type, configurationKey],
    );
    const requestKey = useMemo(
        () => (queryConfiguration ? getReportComponentDatasetRequestKey(queryStateKey, queryConfiguration) : undefined),
        [queryConfiguration, queryStateKey],
    );

    useEffect(() => {
        if (publicQueryResult) {return;}
        if (!readiness.isReady || !queryConfiguration || !requestKey) {return;}

        queryDatasets(queryStateKey, queryConfiguration, requestKey);
    }, [publicQueryResult, queryConfiguration, queryDatasets, queryStateKey, readiness.isReady, requestKey]);

    const dataMatchesCurrentRequest = readiness.isReady && requestKey && queryState?.requestKey === requestKey;

    return {
        data: publicQueryResult ?? (dataMatchesCurrentRequest ? (queryState?.data ?? EMPTY_QUERY_RESULT) : EMPTY_QUERY_RESULT),
        error: queryState?.error,
        loading: publicQueryResult ? false : dataMatchesCurrentRequest ? (queryState?.loading ?? false) : false,
    };
};
